/* eslint-disable */

import React, { useEffect, useState, useCallback } from 'react';
import { Button, Modal, Row, Col, Input, Tooltip, message } from 'antd';
import { isEmpty, findIndex, cloneDeep } from 'lodash';
import { arrayOf, array, func, string, bool, shape, number } from 'prop-types';
import { generateUrl, getKeyValue } from '../../utilities/data_util';
import instance from '../../utilities/axios_util';
import Header from './header';
import ProfileList from './profileList';
import Compare from './compare';
import AddEmail from './addEmail';
import MapFromExternalDatabase from './mapFromExternalDatabase';
import useDebounce from '../../hooks/useDebounce';
import IgnoreStateRow from './ignoreStateRow';
import IgnoreWarning from './ignoreWarning';
import { PlusOutlined } from '@ant-design/icons';
import './style.scss';

let searchInterval = null;
const STEPS = {
  STEP_SEARCH: 1,
  STEP_COMPARE: 2,
  STEP_ADD_EMAIL: 3,
  STEP_MAP_FROM_EXTERNAL_DATABASE: 4,
};
let page = 1;

/**
 * Mapper Service Component
 * This mapper service will take the details of the profile that need to be mapped and show recommendations based on different parameters(nickName, firstName, lastName) etc. using recommendation api and then we can match the record with a profile. We're also using another api to set that record as ignored (if required), we won't map ignored records to any profiles
 * profileApiUrl -> Url for profileApi
 * isCompareEnabled -> is comparing profiles functionality enabled
 * isProfileMappingIgnored -> is mapping ignored for this record
 * isMatched -> is this record matched with a profile
 * isVFXEnabled -> is VFX enabled
 * handleHideModal -> callback to call after hiding modal
 * handleOkModal -> callback to call after mapping profile
 * onIgnore -> callback to call after ignoring profile
 * isMultipleSelectionEnabled -> If true, mapper will allow users to select multiple profiles
 * disableSelectedItems -> Disables selection of profiles that are already selected
 */
function Mapper({
  endpoint: profileApiUrl,
  isCompareEnabled,
  activityData,
  isVFXEnabled,
  handleHideModal,
  handleOkModal,
  onIgnore,
  initialRecommendationSize,
  isIgnoreEnabled,
  isMultipleSelectionEnabled,
  selectedItems,
  initialStep = STEPS.STEP_SEARCH,
  record = {},
  handleMultiSelectionOk,
  isSearchFromExternalDatabaseEnabled = false,
  externalDatabaseConfig = {
    name: window.isExternalDatabaseBBCRM ? 'BBCRM' : "Raiser's Edge",
  },
  successMessage = 'Mapped to record',
  searchStepText = 'Select Record',
  disableSelectedItems = false,
  bottomText = null,
  ...props
}) {
  const { isProfileMappingIgnored, isMatched, title, subTitle } = activityData;
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState('');
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [profileData, setProfileData] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(record);
  const debouncedQuery = useDebounce(query, 500);
  const [isIgnored, setIsIgnored] = useState(isProfileMappingIgnored);
  const [selectedRecords, setSelectedRecords] = useState([...selectedItems]);
  const defaultQueryValue = getKeyValue(props, 'query', '');

  /**
   * This function will toggle modal and will call handleHideModal, which will run any post modal close task
   */
  function toggleModal() {
    if (visible && handleHideModal) {
      handleHideModal();
    }
    setVisible(!visible);
  }

  /**
   *
   * @param {number} pageValue
   * @param {string} queryValue
   * @param {bool} shouldUseInitialSize
   * This will fetch profiles based on query parameter and page number
   * this function will fetch profiles based on queryValue
   * If shouldInitialSize is true it'll fetch only (initialRecommendationSize) number of records, otherwise default backend value
   */
  function fetchProfiles(pageValue = page, queryValue = debouncedQuery, shouldUseInitialSize = false) {
    if (!hasMore) {
      if (pageValue === 1) setHasMore(true);
      else return;
    }
    if (!loading) {
      setLoading(true);
    }
    // if vfx is enabled we need to add groups as fields to get verification groups
    // we can not add groups by default to fields as if vfx is not enabled, it will take 40-50 ms more to fetch data for groups
    let apiFields = props.fields;
    if (isVFXEnabled) {
      apiFields += ',groups';
    }
    const apiUrl = generateUrl({
      url: profileApiUrl,
      page: pageValue,
      query: queryValue,
      filters: props.filters,
      sorter: '&ordering=-id',
      fields: apiFields,
      ...(shouldUseInitialSize ? { size: initialRecommendationSize } : { size: 5 }),
    });
    instance(apiUrl, 'GET')
      .then((response) => {
        const recordsValue = response.data.results;
        let previousRecords = JSON.parse(JSON.stringify(records));
        if (pageValue === 1 || response.data.previous == null) {
          previousRecords = [];
        }
        const updatedRecords = previousRecords.concat(recordsValue);
        setLoading(false);
        setRecords(updatedRecords);
        setHasMore(response.data.next);
        page = pageValue + 1;
      })
      .catch(() => {
        setLoading(false);
        setHasMore(false);
      });
  }

  /**
   * We have two main variables here
   * 1. debouncedQuery -> query value of search component
   * 2. defaultQueryValue -> query value passed from parent for the record
   * Now we have certain conditions for these variables.
   * -> when debouncedQuery has some value -> we'll show recommendation for the query value
   * -> when debouncedQuery hasn't any value,then
   *    -> if defaultQueryValue has some value -> we'll show recommendation for tha value
   *    -> we'll show empty block
   */
  useEffect(() => {
    if (debouncedQuery) {
      setLoading(true);
      setHasMore(true);
      fetchProfiles(1, debouncedQuery);
    } else {
      if (defaultQueryValue) {
        setLoading(true);
        fetchProfiles(1, defaultQueryValue, true);
      } else {
        setLoading(false);
        setRecords([]);
      }
    }
  }, [debouncedQuery]);

  /**
   * This function will refresh recommendations
   */
  function onRefresh() {
    if (!debouncedQuery && defaultQueryValue) {
      setLoading(true);
      fetchProfiles(1, defaultQueryValue, true);
    }
  }
  /**
   *
   * @param {event} e
   * This function will check for input in search and will change states accordingly
   */
  function queryProfiles(e) {
    // eslint-disable-next-line no-control-regex
    // this regex will check if the entered key is valid (A-Z, a-z,, 0-9, backspace) these types of keys are valid and keys like !,$ and mostly other special characters are invalid
    const regex = new RegExp('^[a-zA-Z0-9\b]+$');
    const str = String.fromCharCode(!e.charCode ? e.which : e.charCode);
    const searchQuery = e.target.value;
    const enterKeyValue = 13;
    if (regex.test(str) || e.which === enterKeyValue) {
      setQuery(searchQuery.trim());
    }
  }

  /**
   *
   * @param {element} ele
   * This function will add keyup event listener on search bar.
   * We don't know when search bar is completely loaded in the page so we'll need to check using javascript getElementById method and then focus on it.
   */
  function setEventListenerToElement(ele) {
    ele.addEventListener('keyup', (event) => {
      queryProfiles(event);
    });
  }

  /**
   * this useEffect is doing two things
   * 1. setting a interval function which will wait till mapper search is loaded in html and then call setEventListenerToElement Function on that element and then clear that interval
   * 2. getting recommendations by calling fetchRecommendations
   */
  useEffect(() => {
    searchInterval = window.setInterval(() => {
      const ele = document.getElementById('mapper-search-id');
      if (ele) {
        setEventListenerToElement(ele);
        clearInterval(searchInterval);
      }
    }, 300);
    return () => {
      clearInterval(searchInterval);
    };
  }, []);

  /**
   * This function prepares the comparison data for mapper service
   * this data has following attributes
   * collapsibleData - this data will be able to collapse and this will contain key value pair in format [{ label: 'Label 1', value: 'Value 1'}]
   * nonCollapsibleData - this data will not be able to collapse and this will contain key value pair in format [{ label: 'Label 1', value: 'Value 1'}]
   * collapsibleDataTitle - title of title of collapsibleDataPanel
   */
  function getProfileData(record) {
    const nonCollapsibleData = [
      {
        label: 'Emails',
        value: record.email_addresses,
        type: 'email_addresses',
      },
      {
        label: 'Phone Number',
        value: record.phone_numbers.find((val) => val.type === 0)?.number,
      },
      {
        label: 'Current Location',
        value: record.location?.verbose_name,
      },
      {
        label: 'Nickname',
        value: record.nick_name,
      },
      {
        label: 'Maiden Name',
        value: record.maiden_name,
      },
    ];
    return {
      collapsibleData: [],
      nonCollapsibleData,
      collapsibleDataTitle: 'View more details',
    };
  }

  /**
   * This function will show successful message after every mapping
   */
  function showSuccessMessage() {
    message.success(successMessage, 3);
  }

  /**
   * Add email callback
   */
  function onAddEmail() {
    setCurrentStep(1);
    handleHideModal();
    message.success('Mapping confirmed and new email added to record.', 3);
  }

  /**
   *
   * @param {record} record
   * if isCompare is Enabled this will open compare modal else it will just spit back profile
   */
  function toggleItemSelection(record) {
    if (isCompareEnabled) {
      setSelectedRecord(record);
      setProfileData({ ...getProfileData(record) });
      setCurrentStep(currentStep + 1);
    } else {
      if (isMultipleSelectionEnabled) {
        const index = findIndex(selectedRecords, (val) => val.id === record.id);
        if (index === -1) {
          selectedRecords.push(record);
        } else {
          selectedRecords.splice(index, 1);
        }
        setSelectedRecords(selectedRecords.slice());
      } else {
        handleOkModal(record);
        showSuccessMessage();
        toggleModal();
      }
    }
  }

  function mapRecord() {
    setCurrentStep(1);
    handleOkModal(selectedRecord);
    showSuccessMessage();
    handleHideModal();
  }

  function mapRecordAndNotClose() {
    handleOkModal(selectedRecord);
  }

  function back() {
    if (initialStep === STEPS.STEP_ADD_EMAIL) {
      handleHideModal();
    }
    if (currentStep === STEPS.STEP_SEARCH) {
      handleHideModal();
    }
    if (currentStep === STEPS.STEP_MAP_FROM_EXTERNAL_DATABASE) {
      setCurrentStep(STEPS.STEP_SEARCH);
    } else {
      setCurrentStep(currentStep - 1);
    }
  }

  function onIgnoreStateChange(val) {
    setIsIgnored(val);
    if (!val) {
      onIgnore(false);
    }
  }

  function handleIgnoreRecord() {
    onIgnore(true);
    toggleModal();
  }

  function handleMultipleSelection() {
    handleOkModal(selectedRecords);
    handleMultiSelectionOk(selectedRecords);
    toggleModal();
  }

  const titlePresent = title !== 'Unidentified' && title;
  const modalSubtitle =
    (titlePresent ? title : '') + (titlePresent && subTitle ? ': ' : '') + (subTitle ? subTitle : '');

  function getCreateProfileUrl(activityData) {
    let url = '/profile/create';

    if (!isEmpty(activityData)) {
      url = `${url}?first_name=${activityData.firstName || ''}&last_name=${activityData.lastName || ''}&email=${
        activityData.email || ''
      }`;
    }

    return url;
  }
  return (
    <Modal
      className="mapper-modal"
      closable={false}
      footer={null}
      bodyStyle={{ padding: 0, minHeight: 570, backgroundColor: '#fff' }}
      width={400}
      visible={visible}
      onCancel={() => toggleModal()}>
      <If condition={currentStep === STEPS.STEP_ADD_EMAIL}>
        <Header
          onBack={back}
          title="Add New Email To Record"
          subtitle="Set email preferences  and inform the user"
          shouldHaveRefresh={false}
        />
      </If>
      <If condition={currentStep === STEPS.STEP_SEARCH}>
        <Header title={searchStepText} onBack={back} onRefresh={onRefresh} subtitle={modalSubtitle} />
      </If>
      <If condition={currentStep === STEPS.STEP_COMPARE}>
        <Header
          onBack={back}
          onRefresh={onRefresh}
          title="Compare Details"
          subtitle="Check if you’ve mapped the right record"
          shouldHaveRefresh={false}
        />
      </If>
      <If condition={currentStep === STEPS.STEP_SEARCH && !isMatched && isIgnoreEnabled}>
        <IgnoreStateRow defaultSwitchState={isIgnored} onIgnoreStateChange={onIgnoreStateChange} />
      </If>
      <If condition={isIgnored}>
        <IgnoreWarning />
      </If>
      <If condition={isIgnored || currentStep === STEPS.STEP_SEARCH}>
        <Row
          className={
            'mapper-search-row ' +
            ((isIgnored || currentStep === STEPS.STEP_COMPARE || currentStep === STEPS.STEP_ADD_EMAIL) && 'arc-d-none')
          }>
          <Col span={21} className="mapper-search">
            <Input.Search
              placeholder="Search by name or email"
              size="large"
              defaultValue=""
              className="mapper-search"
              id="mapper-search-id"
            />
          </Col>
          <Col span={3} className="arc-text-align-c mapper-icon">
            <Tooltip title="Add new record">
              <a href={getCreateProfileUrl(activityData)} target="_blank">
                <PlusOutlined className="mapper-add-icon" />
              </a>
            </Tooltip>
          </Col>
        </Row>
      </If>
      <If condition={!isIgnored && currentStep === STEPS.STEP_SEARCH}>
        <ProfileList
          records={records}
          hasMore={hasMore}
          loading={loading}
          loadMore={fetchProfiles}
          isVFXEnabled={isVFXEnabled}
          selectedItems={selectedItems}
          selectedRecords={selectedRecords}
          toggleItemSelection={toggleItemSelection}
          disableSelectedItems={disableSelectedItems}
          isMultipleSelectionEnabled={isMultipleSelectionEnabled}
          hasRecommendations={query.length === 0 && defaultQueryValue}
          isEmptyDefaultQueryState={!defaultQueryValue && query.length === 0}
          emptyBlockTextArr={
            !defaultQueryValue && query.length === 0
              ? ['Find the record on your database for this person by searching using their name or email']
              : [
                  'We could not find any records that matched.',
                  'Try searching using different keywords or use the plus button above to create a new record.',
                ]
          }
        />
        <If condition={isSearchFromExternalDatabaseEnabled && !loading}>
          <div className="external-database-wrapper">
            <p className="arc-H150 arc-color-black label">Can’t find the constituent you are looking for?</p>
            <Button
              className="arc-btn-subtle arc-color-primary mt8"
              block
              onClick={() => setCurrentStep(STEPS.STEP_MAP_FROM_EXTERNAL_DATABASE)}>
              Find constituent on {externalDatabaseConfig.name} instead
            </Button>
          </div>
        </If>
      </If>
      <If condition={currentStep === STEPS.STEP_COMPARE}>
        <Compare
          activityData={activityData}
          profileData={profileData}
          record={selectedRecord}
          isVFXEnabled={isVFXEnabled}
        />
        <div className="compare-footer arc-page-bg mb24">
          <Button type="primary" size="large" block onClick={mapRecord}>
            Confirm and map to record
          </Button>
          <If condition={activityData.isNewEmail}>
            <Button
              size="large"
              block
              className="mt8 success-btn"
              onClick={() => {
                mapRecordAndNotClose();
                setCurrentStep(STEPS.STEP_ADD_EMAIL);
              }}>
              Confirm mapping. Also add email to record
            </Button>
          </If>
          <Choose>
            <When condition={!activityData.isNewEmail}>
              <p className="arc-p arc-font-weight-400 mt16">
                This is just a soft-link and will not alter any data on your source record. You can unmap or change this
                mapping later if you wish.
              </p>
            </When>
            <Otherwise>
              <p className="arc-p arc-font-weight-400 mt16">
                Choosing to add {activityData.email} to the mapped record, will let you set preferences on the next
                step.
              </p>
              <p className="arc-p arc-font-weight-400 mt16">
                If you’re unsure, you can choose to complete only the mapping now and add the email to record later.
              </p>
            </Otherwise>
          </Choose>
        </div>
      </If>
      <If condition={currentStep === STEPS.STEP_ADD_EMAIL}>
        <AddEmail
          record={selectedRecord}
          isVFXEnabled={isVFXEnabled}
          activityData={activityData}
          onAddEmail={onAddEmail}
        />
      </If>
      <If condition={isIgnored}>
        <Button
          type="primary"
          className="warning-done-button arc-color-primary"
          size="large"
          block
          onClick={handleIgnoreRecord}>
          Done
        </Button>
      </If>
      <If condition={isMultipleSelectionEnabled && !isEmpty(selectedRecords)}>
        <Button
          type="primary"
          className="warning-done-button arc-color-primary proceed-with-selection-btn"
          size="large"
          block
          onClick={handleMultipleSelection}>
          <span> {bottomText ?? 'Proceed with selection'} </span>
          <div className="badge arc-support">{selectedRecords.length}</div>
        </Button>
      </If>
      <If condition={currentStep === STEPS.STEP_MAP_FROM_EXTERNAL_DATABASE}>
        <MapFromExternalDatabase
          externalDatabaseConfig={externalDatabaseConfig}
          handleOk={handleOkModal}
          goBack={back}
        />
      </If>
    </Modal>
  );
}

Mapper.propTypes = {
  endpoint: string,
  handleOkModal: func,
  filters: string,
  fields: string,
  handleHideModal: func,
  isCompareEnabled: bool,
  activityData: shape({
    firstName: string,
    lastName: string,
    email: string,
    collapsibleData: array,
    nonCollapsibleData: array,
    collapsibleDataTitle: string,
  }),
  isVFXEnabled: bool,
  initialRecommendationSize: number,
  isIgnoreEnabled: bool,
  isMultipleSelectionEnabled: bool,
  selectedItems: arrayOf(shape()),
  handleMultiSelectionOk: func,
};
Mapper.defaultProps = {
  endpoint: 'profiles',
  filters: '&active=true&email__isnull=false',
  fields:
    '&fields=can_email,external_database_id,external_database_lookup_id,formatted_name,active,email_addresses,email_addresses__status,email_addresses__is_primary,address,slug,id,role,profile_url,location,nick_name,maiden_name,present_picture,educations__class_year,listed,is_registered,phone_numbers__number,phone_numbers__type',
  handleHideModal: () => {},
  handleOkModal: () => {},
  isCompareEnabled: true,
  activityData: {},
  isVFXEnabled: false,
  initialRecommendationSize: 3,
  isIgnoreEnabled: true,
  isMultipleSelectionEnabled: false,
  selectedItems: [],
  handleMultiSelectionOk: () => {},
};

export default Mapper;
